import { Alert, Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { SignatureConfirmDialog } from '../../../components/signature/SignatureConfirmDialog';
import { RoutesList } from '../../../config/routes-list';
import { useIsMobile } from '../../../hooks/use-is-mobile';
import { useNotification } from '../../../hooks/use-notification';
import { WizardNavigation } from '../components/wizard-navigation';
import { useCloseDeliveryNote } from '../hooks/use-delivery-note-mutations';
import { getStepRoute, useWizardDraft } from '../hooks/use-wizard-draft';
import { ComplianceSummary } from './summary-step/compliance-summary';
import { LocalItemsSection } from './summary-step/local-items-section';
import { SummaryMasthead } from './summary-step/summary-masthead';
import { WizardLayout } from './wizard-layout';

export function SummaryStepPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { showError } = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draftId');

  const { data: draft } = useWizardDraft(draftId);
  const closeDeliveryNote = useCloseDeliveryNote();
  const [dialogOpen, setDialogOpen] = useState(false);

  const items = draft?.items ?? [];
  const hasItems = items.length > 0;
  const canConfirm = hasItems && !!draft?.supplier;

  const handleConfirm = async () => {
    if (!draftId) {
      showError(t('goodsReceiving.supplierNotSelected'));
      return;
    }
    await closeDeliveryNote.mutateAsync(draftId);
    setDialogOpen(false);
    navigate(`${RoutesList.GOODS_RECEIVING}/history`, { flushSync: true });
  };

  // flushSync commits the new step's DOM synchronously with the URL change:
  // react-router otherwise renders the route change as a startTransition,
  // leaving the PREVIOUS step (with its own same-labeled "Indietro") mounted
  // and clickable while the URL already shows this step — the click then hits
  // the stale step's handler. See goods-receiving.routes.wizard-back.test.tsx.
  const handleBack = () => {
    navigate(`${getStepRoute('storage')}?draftId=${draftId}`, { flushSync: true });
  };

  const handleNext = () => {
    setDialogOpen(true);
  };

  return (
    <WizardLayout>
      <Stack spacing={2.5} sx={{ pb: 12 }}>
        <Box sx={{ px: 0.5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('goodsReceiving.summarySubtitle')}
          </Typography>
        </Box>

        <SummaryMasthead
          supplierName={draft?.supplier?.name ?? null}
          documentNumber={draft?.documentNumber ?? null}
          documentDate={draft?.date ?? null}
        />

        <ComplianceSummary
          checklist={{
            temperature: draft?.temperature ?? null,
            vehicleHygiene: draft?.vehicleHygiene ?? true,
            packagingIntact: draft?.packagingIntact ?? true,
            notes: draft?.notes ?? null,
          }}
        />

        {hasItems ? (
          <LocalItemsSection items={items} />
        ) : (
          <Alert
            severity="warning"
            sx={{
              '& .MuiAlert-message': {
                fontSize: isMobile ? '0.875rem' : undefined,
              },
            }}
          >
            {t('goodsReceiving.addItemFirst')}
          </Alert>
        )}
      </Stack>

      <WizardNavigation
        canGoBack={true}
        canGoNext={canConfirm && !closeDeliveryNote.isPending}
        onBack={handleBack}
        onNext={handleNext}
        nextLabel={
          closeDeliveryNote.isPending
            ? t('goodsReceiving.creating')
            : t('goodsReceiving.confirmDdt')
        }
        isLoading={closeDeliveryNote.isPending}
      />

      <SignatureConfirmDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        recordCount={items.length}
        title={t('goodsReceiving.confirmDdtTitle')}
        description={t('goodsReceiving.confirmDdtDesc', { count: items.length })}
      />
    </WizardLayout>
  );
}
